import { graph } from '@adc/constants'
import { formatMoney } from '../../../../utils/formatters'
// @ts-ignore base is jsx, no type declarations
import ListingBadge, { BADGE_ORDER_PRIORITY, BADGE_MODE } from './base'
import { getAssetType } from './utils'

const EMD_FLAG = 'fe.resi.pdp.enable_pdp_emd_instructions'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listing = any

const isAssetTypeBankOwned = (listing: Listing): boolean =>
  getAssetType(listing) === graph.enums.AssetType.BANK_OWNED

const isSmartSale = (listing: Listing): boolean =>
  Boolean(listing.program_configuration?.is_make_an_offer_enabled)

const emdPrimaryAmount = (listing: Listing): number | undefined =>
  listing.program_configuration?.earnest_money_deposit_primary_amount

const emdMinimumAmount = (listing: Listing): number | undefined =>
  listing.program_configuration?.earnest_money_deposit_minimum_amount

const isFlatRateEmd = (listing: Listing): boolean =>
  listing.program_configuration?.earnest_money_deposit_commission_type ===
  graph.enums.CommissionStrategyType.DOLLAR

class EarnestMoneyDepositReoBadge extends ListingBadge {
  static id = 'EarnestMoneyDepositReoBadge'

  static title = 'Earnest Money'

  static titleIcon = 'money-check-dollar'

  static contentPositive = false

  static renderPriority = BADGE_ORDER_PRIORITY.FOURTH

  static tooltipContent =
    'A deposit placed by the buyer to demonstrate commitment to purchasing the property.'

  toggleContent = () => {
    // @ts-ignore inherited from ListingBadge base class (jsx)
    const wasCollapsed = !this.state.showContent
    // @ts-ignore inherited from ListingBadge base class (jsx)
    this.setState({ showContent: !this.state.showContent })
    if (wasCollapsed) {
      // @ts-ignore inherited from ListingBadge base class (jsx)
      this.emitEvent('emd_badge_expanded')
    }
  }

  static shouldBeIncluded(
    listing: Listing,
    badgeMode: string,
    featureFlags: Record<string, unknown> = {}
  ): boolean {
    return (
      badgeMode === BADGE_MODE.EXPANDED &&
      Boolean(featureFlags[EMD_FLAG]) &&
      isAssetTypeBankOwned(listing) &&
      !isSmartSale(listing) &&
      Boolean(
        listing.listing_configuration?.is_earnest_money_deposit_available
      ) &&
      Boolean(emdPrimaryAmount(listing) || emdMinimumAmount(listing))
    )
  }

  get summary(): string {
    // @ts-ignore inherited from ListingBadge base class (jsx)
    const { listing } = this.props
    const primaryAmount = emdPrimaryAmount(listing)
    const minimumAmount = emdMinimumAmount(listing)

    // Flat-rate EMD: primaryAmount is a dollar figure, not a percentage.
    if (isFlatRateEmd(listing) && primaryAmount) {
      return formatMoney(primaryAmount)
    }

    if (!primaryAmount) {
      return minimumAmount ? formatMoney(minimumAmount) : ''
    }
    if (!minimumAmount) {
      return `${primaryAmount}%`
    }
    return `${primaryAmount}% or ${formatMoney(minimumAmount)}`
  }

  get content(): string {
    // @ts-ignore inherited from ListingBadge base class (jsx)
    const { listing } = this.props
    const primaryAmount = emdPrimaryAmount(listing)
    const minimumAmount = emdMinimumAmount(listing)

    let amountDescription = ''
    if (isFlatRateEmd(listing) && primaryAmount) {
      // Flat-rate EMD: primaryAmount is a dollar figure, not a percentage.
      amountDescription = formatMoney(primaryAmount)
    } else if (primaryAmount && minimumAmount) {
      amountDescription = `${primaryAmount}% of total purchase price (or ${formatMoney(
        minimumAmount
      )}, whichever is greatest)`
    } else if (primaryAmount) {
      amountDescription = `${primaryAmount}% of total purchase price`
    } else if (minimumAmount) {
      amountDescription = formatMoney(minimumAmount)
    }

    const amountSentence = `Winning bidder must provide earnest money deposit of ${amountDescription}.`

    const timelineLabel =
      listing.closing_configuration?.earnest_money_deposit_timeline_label

    if (timelineLabel) {
      return `${amountSentence} ${timelineLabel}`
    }

    return amountSentence
  }
}

export default EarnestMoneyDepositReoBadge
